import FingerprintJS from '@fingerprintjs/fingerprintjs';

/**
 * Generate a unique browser fingerprint for free trial abuse prevention
 *
 * Uses FingerprintJS OSS to generate a visitorId, then hashes it with SHA-256
 * for privacy protection before sending to the backend.
 *
 * @returns {Promise<string | null>} SHA-256 hash of the fingerprint, or null if unavailable
 *
 * @example
 * const fingerprint = await generateFingerprint();
 * if (fingerprint) {
 *   // Include in registration payload
 *   registerUser({ ...data, device_fingerprint: fingerprint });
 * }
 */
export async function generateFingerprint(): Promise<string | null> {
  try {
    // Load FingerprintJS agent
    const fp = await FingerprintJS.load();

    // Get the visitor identifier
    const result = await fp.get();

    // Hash the visitorId with SHA-256 for privacy
    const encoder = new TextEncoder();
    const data = encoder.encode(result.visitorId);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert hash buffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    return hashHex;
  } catch (error) {
    // Graceful degradation - allow registration without fingerprint
    // This handles privacy browsers (Brave, Tor) that block fingerprinting
    console.error('Fingerprint generation failed:', error);
    return null;
  }
}
